import React, { useMemo } from 'react';
import {
  AiVideo, Question, AiEngine, Warning,
} from '@picsio/icons';
import {
  string, oneOf, bool, oneOfType, number,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon } from '@picsio/ui';
import Spinner from '../Spinner';
import Tooltip from '../../../Tooltip';
import { parseTime } from '../../../../shared/utils';
import { sendAssetsToTranscribe } from '../../../../store/actions/assets';
import l18n from '../../../../shared/strings';
import ua from '../../../../ua';
import Logger from '../../../../services/Logger';
import { navigate } from '../../../../helpers/history';
import helpersLink from '../../../Billing/Overview/Features/constants/helpersLink';
import { isHaveTeammatePermission } from '../../../../store/helpers/user';
import options from '../ViewsSelector/options';
import { calculateAiMinutes } from '../../../../helpers/calculateAICredits';

function TranscriptPlaceholder({
  assetId,
  assetName,
  assetSize,
  assetMimeType,
  view = 'transcript',
  jobStatus = 'skipped',
  transcriptPermission = false,
  activeRevisionID = null,
  isLoaded = false,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user || {});
  const defaultLanguage = useSelector((state) => state.user.team?.policies?.transcribingLanguage);
  const isAllowedTranscribe = useSelector((state) => state.user.subscriptionFeatures.transcribe);
  const isManageBillingAllow = isHaveTeammatePermission('manageBilling', user);
  const aiMinutes = useMemo(() => calculateAiMinutes(user.team), [user]);
  const aiMinutesString = useMemo(
    () => parseTime(aiMinutes * 60, { type: 'hoursAndMinutes' }),
    [aiMinutes],
  );

  const viewTitle = useMemo(() => options.find((option) => option.value === view)?.text || '', [view]);

  const handleTranscribe = () => {
    Logger.log('User', 'VideoIntelligenceAnalyzeClicked', {
      assetId,
      assetName,
      language: defaultLanguage || 'auto_detect',
      asset_size: assetSize,
      asset_format: assetMimeType,
      tabName: view,
    });
    dispatch(sendAssetsToTranscribe({ assetIds: [assetId], language: defaultLanguage }));
  };

  const handleClickUpgradePlan = () => {
    Logger.log('User', 'TranscriptUpgradePlanClicked');
    navigate('/billing?tab=overview');
  };

  const isError = useMemo(
    () => jobStatus === 'failed' || jobStatus === 'rejected',
    [jobStatus],
  );

  const isRunning = useMemo(
    () => jobStatus === 'waiting' || jobStatus === 'running',
    [jobStatus],
  );

  return (
    <div className="TranscriptPlaceholder">
      <div className={`Placeholder ${!isAllowedTranscribe ? 'notUpgrade' : ''}`}>
        <Choose>
          <When condition={!isAllowedTranscribe}>
            <AiVideo width="80" height="80" className="Placeholder__icon" />
          </When>
          <When condition={isRunning || activeRevisionID === null || !isLoaded}>
            <Spinner />
          </When>
          <When condition={isError}>
            <Warning width="80" height="80" className="Placeholder__icon Placeholder__icon--warning" />
          </When>
          <Otherwise>
            <AiVideo width="80" height="80" className="Placeholder__icon" />
          </Otherwise>
        </Choose>
        <If condition={isLoaded}>
          <h3 className="Placeholder__title">
            <Choose>
              <When condition={isRunning}>
                {l18n.TRANSCRIPT.placeholderRunningTitle(viewTitle)}
              </When>
              <When condition={isError}>
                {l18n.TRANSCRIPT.placeholderErrorTitle(viewTitle)}
              </When>
              <Otherwise>
                {l18n.TRANSCRIPT.placeholderTitle}
              </Otherwise>
            </Choose>
          </h3>
          <If condition={!isRunning}>
            <p className="Placeholder__description">
              {isError ? l18n.TRANSCRIPT.placeholderErrorDescription : l18n.TRANSCRIPT.placeholderDecsription}
            </p>
          </If>
          <If condition={!ua.browser.isNotDesktop()
              && !isRunning
              && activeRevisionID !== null
              && isLoaded
              && isAllowedTranscribe}
          >
            <Tooltip
              content={transcriptPermission
                ? l18n.DETAILS.aiIconMinutesTooltipText(null, aiMinutesString)
                : l18n.DETAILS.aiIconNoPermissionTooltipText()}
              placement="top"
            >
              <Button
                dataTestId="btnPreviewChangeTranscriptLang"
                onClick={handleTranscribe}
                variant="contained"
                size="md"
                color="primary"
                style={{ minWidth: 150 }}
                startIcon={<AiEngine />}
                disabled={!transcriptPermission}
              >
                {l18n.TRANSCRIPT.placeholderButton}
              </Button>
            </Tooltip>
          </If>
        </If>
      </div>
      <If condition={!isAllowedTranscribe}>
        <div className="Placeholder__upgradePlanContainer">
          <a
            href={helpersLink.transcribe}
            target="_blank"
            rel="noreferrer"
            className="Placeholder__helperLink"
          >
            <Icon color="primary" size="sm">
              <Question />
            </Icon>
            <span className="Placeholder__helperText">
              {l18n.UPGRADE_PLAN_BLOCK.transcript.helperText}
            </span>
          </a>
          <If condition={!ua.isMobileApp()}>
            <Button
              id="upgradeTranscribe"
              onClick={handleClickUpgradePlan}
              variant="contained"
              size="md"
              color="primary"
              disabled={!isManageBillingAllow}
            >
              {l18n.BILLING.textUpgradePlan}
            </Button>
          </If>
        </div>
      </If>
    </div>
  );
}

TranscriptPlaceholder.propTypes = {
  assetId: string.isRequired,
  assetName: string.isRequired,
  jobStatus: oneOf([
    'skipped',
    'waiting',
    'running',
    'complete',
    'failed',
    'rejected',
  ]),
  view: string,
  assetSize: oneOfType([number, string]),
  assetMimeType: string,
  transcriptPermission: bool,
  activeRevisionID: string,
  isLoaded: bool,
};

export default TranscriptPlaceholder;
